import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Building2, MapPin, DollarSign, Ruler, Zap, CheckCircle2, XCircle, Upload, Loader2,
  Phone, Mail, Send, FileText, X, ChevronDown, Layers, Scale,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO = "/logo.png";
const TEAL = "#27BEAA";
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : null);
const SUBMITTER_TYPES = ["Wholesaler", "Agent", "Broker", "Property Owner", "Builder", "Developer", "Other"];
const INP = "bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#27BEAA]";

function Collapse({ title, defaultOpen = false, testid, children, icon: Icon }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
      >
        <span className="flex items-center gap-2.5">
          {Icon && <Icon className="h-4 w-4" style={{ color: TEAL }} />}
          <span className="text-sm font-semibold tracking-wide uppercase text-white/90">{title}</span>
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} style={{ color: TEAL }} />
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/10 pt-4">{children}</div>}
    </div>
  );
}

const Metric = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4">
    <div className="flex items-center gap-2 text-white/40 text-[11px] uppercase tracking-wider">
      <Icon className="h-3.5 w-3.5" style={{ color: TEAL }} /> {label}
    </div>
    <div className="text-2xl font-bold mt-1.5 tracking-tight text-white">{value}</div>
  </div>
);

const Chip = ({ children }) => (
  <span className="text-sm px-3 py-1.5 rounded-full bg-white/[0.06] text-white/85 border border-white/10">{children}</span>
);
const Row = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wider text-white/40">{label}</span>
    <span className="text-sm text-white/90 mt-0.5">{value}</span>
  </div>
);

export default function PublicBuyBox() {
  const { slug } = useParams();
  const [bb, setBb] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const fileRef = useRef();
  const [form, setForm] = useState({
    submitter_name: "", company: "", submitter_type: "Wholesaler", submitter_email: "", phone: "",
    property_address: "", apn: "", city: "", state: "", postal_code: "",
    asking_price: "", assignment_fee: "", lot_size_sqft: "", acreage: "", zoning: "",
    property_type: "", utilities: "", road_access: "", property_description: "",
    closing_deadline: "", listing_url: "",
  });

  useEffect(() => {
    document.title = "ARQITEK — Submit a Property";
    axios.get(`${BACKEND}/api/public/buybox/${slug}`)
      .then((r) => setBb(r.data))
      .catch(() => setError("This Buy Box is not available."));
  }, [slug]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    for (const file of picked) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${BACKEND}/api/public/buybox/${slug}/upload`, fd);
        setFiles((prev) => [...prev, data]);
      } catch { /* skip failed file */ }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!form.submitter_email.trim()) { alert("Please enter your email."); return; }
    if (!form.property_address.trim() && !form.apn.trim()) { alert("Please enter a property address or APN."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/buybox/${slug}/submit`, { ...form, files });
      setSubmitted(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#08100E] flex flex-col items-center justify-center text-center px-6" data-testid="public-buy-box-page">
        <img src={LOGO} alt="ARQITEK" className="h-8 mb-6 opacity-90" />
        <h1 className="text-xl font-semibold text-white">This page is unavailable</h1>
        <p className="text-white/50 mt-2 text-sm">The link may have expired or is incorrect. Please contact ARQITEK.</p>
      </div>
    );
  }
  if (!bb) {
    return <div className="min-h-screen bg-[#08100E] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: TEAL }} /></div>;
  }

  const g = bb.geography || {};
  const markets = [...(g.cities || []), ...(g.counties || [])];
  const c = bb.criteria || {};
  const priceRange = money(c.max_price) ? `Up to ${money(c.max_price)}` : (money(c.min_price) ? `From ${money(c.min_price)}` : "—");
  const lotRange = c.min_lot_sf || c.max_lot_sf
    ? `${c.min_lot_sf ? Number(c.min_lot_sf).toLocaleString() : "0"}–${c.max_lot_sf ? Number(c.max_lot_sf).toLocaleString() : "∞"} SF` : null;
  const ct = bb.contact || {};

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#08100E] flex items-center justify-center p-6" data-testid="public-buy-box-page">
        <div className="max-w-md text-center rounded-2xl border border-white/10 bg-white/[0.02] p-10" data-testid="submit-success">
          <CheckCircle2 className="h-14 w-14 mx-auto mb-4" style={{ color: TEAL }} />
          <h2 className="text-2xl font-semibold text-white">Property Submitted</h2>
          <p className="text-white/50 mt-2">Thanks — we've received your property and our team will review it against our Buy Box. We'll be in touch if it's a fit.</p>
          <Button className="mt-6 font-medium" style={{ backgroundColor: TEAL, color: "#08100E" }} onClick={() => { setSubmitted(false); setShowForm(false); setFiles([]); }}>Submit Another</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08100E] text-white" data-testid="public-buy-box-page">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-5">
          <img src={LOGO} alt="ARQITEK" className="h-6 w-auto object-contain" />
          <span className="block mt-2 text-[11px] uppercase tracking-[0.2em] text-white/40">Acquisition Criteria</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Title */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            {bb.public_headline || `We're actively buying — ${bb.name}`}
          </h1>
          {bb.public_description && <p className="text-white/60 mt-4 text-base max-w-2xl leading-relaxed">{bb.public_description}</p>}
          {(markets.length > 0 || g.state) && (
            <div className="flex flex-wrap items-center gap-2 mt-5">
              <MapPin className="h-4 w-4" style={{ color: TEAL }} />
              {(markets.length ? markets : [g.state]).filter(Boolean).map((m) => <Chip key={m}>{m}</Chip>)}
            </div>
          )}
          <Button size="lg" className="mt-7 font-medium" style={{ backgroundColor: TEAL, color: "#08100E" }} data-testid="open-submit-form-btn" onClick={() => setShowForm(true)}>
            <Send className="h-4 w-4 mr-2" /> Submit a Property
          </Button>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric icon={DollarSign} label="Purchase Price" value={priceRange} />
          {lotRange && <Metric icon={Ruler} label="Lot Size" value={lotRange} />}
          {(c.min_acreage || c.max_acreage) && <Metric icon={Layers} label="Acreage" value={`${c.min_acreage || 0}–${c.max_acreage || "∞"} ac`} />}
          {(c.min_lots || c.max_lots) && <Metric icon={Building2} label="Potential Lots" value={`${c.min_lots || 1}–${c.max_lots || "∞"}`} />}
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {(bb.property_types || []).length > 0 && (
            <Collapse title="Property Types" defaultOpen icon={Building2}>
              <div className="flex flex-wrap gap-2">{bb.property_types.map((t) => <Chip key={t}>{t}</Chip>)}</div>
            </Collapse>
          )}
          {(bb.development_strategies || []).length > 0 && (
            <Collapse title="Development Opportunities" defaultOpen icon={Ruler}>
              <div className="flex flex-wrap gap-2">{bb.development_strategies.map((t) => <Chip key={t}>{t}</Chip>)}</div>
            </Collapse>
          )}
          {((bb.zoning?.preferred || []).length > 0 || (bb.zoning?.acceptable || []).length > 0 || bb.zoning?.residential_only) && (
            <Collapse title="Zoning" defaultOpen icon={Scale}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {(bb.zoning.preferred || []).length > 0 && <Row label="Preferred" value={bb.zoning.preferred.join(", ")} />}
                {(bb.zoning.acceptable || []).length > 0 && <Row label="Acceptable" value={bb.zoning.acceptable.join(", ")} />}
                {bb.zoning.residential_only && <Row label="Requirement" value="Residential only" />}
              </div>
            </Collapse>
          )}
          {(bb.utilities?.water || bb.utilities?.sewer || bb.utilities?.electric || bb.utilities?.gas) && (
            <Collapse title="Utilities" icon={Zap}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {bb.utilities.water && <Row label="Water" value={bb.utilities.water} />}
                {bb.utilities.sewer && <Row label="Sewer" value={bb.utilities.sewer} />}
                {bb.utilities.electric && <Row label="Electric" value={bb.utilities.electric} />}
                {bb.utilities.gas && <Row label="Gas" value={bb.utilities.gas} />}
              </div>
            </Collapse>
          )}
          {(bb.site?.characteristics || []).length > 0 && (
            <Collapse title="Site Preferences" icon={CheckCircle2}>
              <div className="flex flex-wrap gap-2">{bb.site.characteristics.map((t) => <Chip key={t}>{t}</Chip>)}</div>
            </Collapse>
          )}
          {(bb.exclusions || []).length > 0 && (
            <Collapse title="We Usually Pass On" icon={XCircle}>
              <div className="flex flex-wrap gap-2">
                {bb.exclusions.map((t) => (
                  <span key={t} className="text-sm px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-400/90 border border-amber-500/20">{t}</span>
                ))}
              </div>
            </Collapse>
          )}
        </div>

        {/* Contact */}
        {(ct.email || ct.phone || ct.name) && (
          <div className="flex flex-wrap gap-6 text-sm text-white/60 border-t border-white/10 pt-6">
            {ct.name && <span className="font-medium text-white/90">{ct.name}</span>}
            {ct.email && <a href={`mailto:${ct.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail className="h-4 w-4" style={{ color: TEAL }} /> {ct.email}</a>}
            {ct.phone && <a href={`tel:${ct.phone}`} className="flex items-center gap-1.5 hover:text-white"><Phone className="h-4 w-4" style={{ color: TEAL }} /> {ct.phone}</a>}
          </div>
        )}

        {/* CTA */}
        <div className="rounded-2xl border border-[#27BEAA]/25 bg-[#27BEAA]/[0.06] p-8 text-center">
          <h3 className="text-2xl font-semibold text-white">Have a property that fits?</h3>
          <p className="text-white/60 mt-2">Send it over — you don't need every detail. We'll research the rest.</p>
          <Button size="lg" className="mt-5 font-medium" style={{ backgroundColor: TEAL, color: "#08100E" }} data-testid="open-submit-form-btn-2" onClick={() => setShowForm(true)}>
            <Send className="h-4 w-4 mr-2" /> Submit a Property
          </Button>
        </div>
      </main>

      <footer className="border-t border-white/10 mt-6">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between text-white/30 text-xs">
          <img src={LOGO} alt="ARQITEK" className="h-5 w-auto object-contain opacity-60" />
          <span>© {new Date().getFullYear()} ARQITEK. All submissions reviewed confidentially.</span>
        </div>
      </footer>

      {/* Submission form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="rounded-2xl w-full max-w-2xl my-8 bg-[#0c1512] border border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="submit-form">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h3 className="text-xl font-semibold text-white">Submit a Property</h3>
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{bb.name}</span>
              </div>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-white/40 hover:text-white" /></button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              <p className="text-sm text-white/50">Only your email and a property address (or APN) are required. Everything else is optional.</p>

              <FormGrid>
                <FF label="Your Name"><Input className={INP} data-testid="sf-name" value={form.submitter_name} onChange={(e) => set("submitter_name", e.target.value)} /></FF>
                <FF label="Company"><Input className={INP} value={form.company} onChange={(e) => set("company", e.target.value)} /></FF>
                <FF label="I am a…">
                  <Select value={form.submitter_type} onValueChange={(v) => set("submitter_type", v)}>
                    <SelectTrigger className={INP} data-testid="sf-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{SUBMITTER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Email *"><Input className={INP} data-testid="sf-email" type="email" value={form.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} /></FF>
                <FF label="Phone"><Input className={INP} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></FF>
              </FormGrid>

              <div className="border-t border-white/10 pt-4" />
              <FF label="Property Address *"><Input className={INP} data-testid="sf-address" value={form.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="Address or nearest cross street" /></FF>
              <FormGrid>
                <FF label="APN"><Input className={INP} data-testid="sf-apn" value={form.apn} onChange={(e) => set("apn", e.target.value)} /></FF>
                <FF label="City"><Input className={INP} value={form.city} onChange={(e) => set("city", e.target.value)} /></FF>
                <FF label="State"><Input className={INP} value={form.state} onChange={(e) => set("state", e.target.value)} /></FF>
                <FF label="ZIP"><Input className={INP} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></FF>
                <FF label="Asking / Contract Price"><Input className={INP} data-testid="sf-price" value={form.asking_price} onChange={(e) => set("asking_price", e.target.value)} /></FF>
                <FF label="Your Assignment Fee"><Input className={INP} value={form.assignment_fee} onChange={(e) => set("assignment_fee", e.target.value)} /></FF>
                <FF label="Lot Size (SF)"><Input className={INP} value={form.lot_size_sqft} onChange={(e) => set("lot_size_sqft", e.target.value)} /></FF>
                <FF label="Acreage"><Input className={INP} value={form.acreage} onChange={(e) => set("acreage", e.target.value)} /></FF>
                <FF label="Zoning (if known)"><Input className={INP} value={form.zoning} onChange={(e) => set("zoning", e.target.value)} /></FF>
                <FF label="Property Type"><Input className={INP} value={form.property_type} onChange={(e) => set("property_type", e.target.value)} /></FF>
                <FF label="Utilities (if known)"><Input className={INP} value={form.utilities} onChange={(e) => set("utilities", e.target.value)} /></FF>
                <FF label="Road / Access"><Input className={INP} value={form.road_access} onChange={(e) => set("road_access", e.target.value)} /></FF>
                <FF label="Closing Deadline"><Input className={INP} value={form.closing_deadline} onChange={(e) => set("closing_deadline", e.target.value)} /></FF>
                <FF label="Link to Listing"><Input className={INP} value={form.listing_url} onChange={(e) => set("listing_url", e.target.value)} /></FF>
              </FormGrid>
              <FF label="Property Description / Notes"><Textarea className={INP} rows={3} value={form.property_description} onChange={(e) => set("property_description", e.target.value)} /></FF>

              <div>
                <Label className="text-sm text-white/70">Photos / Documents</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <input ref={fileRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="sf-file-input" />
                  <Button type="button" variant="outline" size="sm" className="border-white/15 bg-white/[0.04] text-white hover:bg-white/10 hover:text-white" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload
                  </Button>
                  <span className="text-xs text-white/30">Photos, aerials, plats, PDFs — optional</span>
                </div>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {files.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white/[0.06] text-white/80">
                        <FileText className="h-3 w-3" /> {f.filename}
                        <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2">
              <Button variant="ghost" className="text-white/60 hover:text-white hover:bg-white/5" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button className="font-medium" style={{ backgroundColor: TEAL, color: "#08100E" }} data-testid="submit-property-btn" onClick={submit} disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Submit Property
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FormGrid = ({ children }) => <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
const FF = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-sm text-white/70">{label}</Label>{children}</div>);
